import React from 'react';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../AppContext';
import GameStatsDashboard from './GameStatsDashboard';

const DreamWeaveInfo = ({ setActiveTab }) => {
    const { t } = useTranslation();
    const { userProfiles, userId } = useAppContext();
    
    // Get current user stats
    const currentUser = userProfiles.find(u => u.id === userId);
    const userStats = currentUser?.stats || {};

    const InfoCard = ({ icon, title, description, onClick }) => {
        const Icon = LucideIcons[icon];
        return (
            <div 
                onClick={onClick}
                className={`bg-white/5 border border-white/10 rounded-xl p-6 transition-all duration-300 hover:bg-white/10 hover:border-cyan-500/30 group ${onClick ? 'cursor-pointer' : ''}`}
            >
                <div className="flex items-center space-x-4 mb-3">
                    <div className="p-3 bg-cyan-500/10 rounded-lg text-cyan-400 group-hover:scale-110 transition-transform">
                        <Icon size={24} />
                    </div>
                    <h3 className="text-lg font-bold text-white font-orbitron">{title}</h3>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
            </div>
        );
    };

    return (
        <div className="max-w-4xl mx-auto space-y-12 pb-12">
            {/* Header Section */}
            <div className="text-center space-y-4">
                <div className="inline-block p-4 bg-purple-500/10 rounded-full mb-4 animate-pulse">
                    <LucideIcons.BrainCircuit size={48} className="text-purple-400" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 font-orbitron">
                    {t('dreamWeaveInfo.header.title')}
                </h2>
                <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                    {t('dreamWeaveInfo.header.subtitle')}
                </p>
            </div>

            {/* How to Weave Section */}
            <div>
                <h3 className="text-2xl font-bold text-white mb-6 pl-2 border-l-4 border-cyan-500">
                    {t('dreamWeaveInfo.howToWeave.title')}
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                    <InfoCard 
                        icon="Flame"
                        title={t('dreamWeaveInfo.howToWeave.auraEater.title')}
                        description={t('dreamWeaveInfo.howToWeave.auraEater.description')}
                        onClick={() => setActiveTab('contribute')}
                    />
                    <InfoCard 
                        icon="Layers"
                        title={t('dreamWeaveInfo.howToWeave.synapticStacking.title')}
                        description={t('dreamWeaveInfo.howToWeave.synapticStacking.description')}
                        onClick={() => setActiveTab('contribute')}
                    />
                    <InfoCard 
                        icon="Spline"
                        title={t('dreamWeaveInfo.howToWeave.synapticSerpent.title')}
                        description={t('dreamWeaveInfo.howToWeave.synapticSerpent.description')}
                        onClick={() => setActiveTab('contribute')}
                    />
                     <InfoCard 
                        icon="Rocket"
                        title={t('dreamWeaveInfo.howToWeave.cosmicAscent.title')}
                        description={t('dreamWeaveInfo.howToWeave.cosmicAscent.description')}
                        onClick={() => setActiveTab('contribute')}
                    />
                    <InfoCard 
                        icon="Shield"
                        title={t('dreamWeaveInfo.howToWeave.starlightSentinel.title')}
                        description={t('dreamWeaveInfo.howToWeave.starlightSentinel.description')}
                        onClick={() => setActiveTab('contribute')}
                    />
                    <InfoCard 
                        icon="MessageSquare"
                        title={t('dreamWeaveInfo.howToWeave.aiCoCreation.title')}
                        description={t('dreamWeaveInfo.howToWeave.aiCoCreation.description')}
                        onClick={() => setActiveTab('aiChat')}
                    />
                </div>
                <div className="mt-8 text-center">
                    <button 
                        onClick={() => setActiveTab('contribute')}
                        className="px-8 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-full font-bold text-white shadow-lg shadow-cyan-500/20 hover:scale-105 transition-transform"
                    >
                        {t('dreamWeaveInfo.goToContributionHub')}
                    </button>
                </div>
            </div>

            {/* Why Contribute Section */}
            <div className="bg-gradient-to-br from-purple-900/20 to-black rounded-2xl p-8 border border-purple-500/20">
                <h3 className="text-2xl font-bold text-white mb-6 text-center">
                    {t('dreamWeaveInfo.whyContribute.title')}
                </h3>
                <div className="grid md:grid-cols-3 gap-8 text-center">
                    <div className="space-y-3">
                        <div className="mx-auto w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center text-yellow-400">
                            <LucideIcons.Sparkles size={24} />
                        </div>
                        <h4 className="font-bold text-white">{t('dreamWeaveInfo.whyContribute.earnSouls.title')}</h4>
                        <p className="text-sm text-gray-400">{t('dreamWeaveInfo.whyContribute.earnSouls.description')}</p>
                    </div>
                    <div className="space-y-3">
                        <div className="mx-auto w-12 h-12 bg-pink-500/20 rounded-full flex items-center justify-center text-pink-400">
                            <LucideIcons.Fingerprint size={24} />
                        </div>
                        <h4 className="font-bold text-white">{t('dreamWeaveInfo.whyContribute.trainAI.title')}</h4>
                        <p className="text-sm text-gray-400">{t('dreamWeaveInfo.whyContribute.trainAI.description')}</p>
                    </div>
                    <div className="space-y-3">
                        <div className="mx-auto w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center text-green-400">
                            <LucideIcons.Gift size={24} />
                        </div>
                        <h4 className="font-bold text-white">{t('dreamWeaveInfo.whyContribute.shareRewards.title')}</h4>
                        <p className="text-sm text-gray-400">{t('dreamWeaveInfo.whyContribute.shareRewards.description')}</p>
                    </div>
                </div>
            </div>

             {/* Personal Career Stats */}
             <GameStatsDashboard userStats={userStats} />

            {/* Conclusion */}
            <div className="text-center space-y-4 pt-8 border-t border-white/10">
                <h3 className="text-3xl font-bold text-white font-orbitron">{t('dreamWeaveInfo.conclusion.title')}</h3>
                <p className="text-gray-400 max-w-2xl mx-auto italic">
                    "{t('dreamWeaveInfo.conclusion.newBeginning.description1')}"
                </p>
                <p className="text-xl text-cyan-400 font-bold mt-4">
                    {t('dreamWeaveInfo.conclusion.newBeginning.description2')}
                </p>
            </div>
        </div>
    );
};

export default DreamWeaveInfo;